<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { confirmReservationBranchLeave } from '@/composition/reservations/reservation-branch-leave';
  import { useReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import { useAuthStore } from '@/composition/use-auth';
  import { useUi } from '@/composition/use-ui';
  import { vueData } from '@/VueData';
  import { maxBookableOnlineParty } from '@/domain/reservations/reservation-capacity';
  import {
    activationBlocker,
    canActivateReservations,
  } from '@/domain/reservations/reservation-guide';
  import type { TActivationBlocker } from '@/domain/reservations/reservation-guide';
  import { PermissionType } from '@/enums/PermissionType';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IBranch } from '@/interfaces/IBranch';
  import type { IUpdateReservationSettingsDto } from '@/api/services/reservations.service';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ReservationsGuideLinkCard from './V2ReservationsGuideLinkCard.vue';
  import V2ReservationsGuideRules from './V2ReservationsGuideRules.vue';
  import V2ReservationsGuideSummary from './V2ReservationsGuideSummary.vue';

  /** The finish screen: ready to activate, live with the link, or on for the team only (§3.5). */
  const context = useReservationGuide();
  const auth = useAuthStore();
  const restaurant = useRestaurantStore();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  /** The check pops once, right after this visit turned reservations on. */
  const justActivated = ref(false);

  const configuration = computed(() => context.configuration.value);
  const state = computed(() => context.finishState.value);
  const busy = computed(() => context.busy.value);
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const branch = computed(() => restaurant.selectedBranch);
  const otherBranches = computed(() =>
    (restaurant.visibleBranches ?? []).filter(
      (candidate) => candidate.id !== branch.value?.id
    )
  );
  const onlineBlocker = computed<TActivationBlocker>(() =>
    configuration.value
      ? activationBlocker(configuration.value, true)
      : 'shifts'
  );
  const phoneOnlyAllowed = computed(
    () => !!configuration.value && canActivateReservations(configuration.value)
  );
  const blockedText = computed(() =>
    onlineBlocker.value === 'shifts'
      ? tr('reservations.guide.finish.blockedShifts')
      : tr('reservations.guide.finish.blockedTables')
  );
  const blockedStep = computed(() =>
    onlineBlocker.value === 'shifts' ? 'hours' : 'tables'
  );
  const blockedBackLabel = computed(() =>
    tr('reservations.guide.finish.backTo', {
      step: tr(`reservations.guide.steps.${blockedStep.value}`),
    })
  );
  const title = computed(() =>
    tr(
      state.value === 'ready'
        ? 'reservations.guide.finish.readyTitle'
        : state.value === 'live'
        ? 'reservations.guide.finish.liveTitle'
        : 'reservations.guide.finish.phoneOnlyTitle'
    )
  );
  const help = computed(() => {
    if (state.value === 'ready')
      return tr(
        canManage.value
          ? 'reservations.guide.finish.readyHelp'
          : 'reservations.guide.finish.readyHelpNoManage'
      );
    if (state.value === 'live')
      return tr(
        canManage.value
          ? 'reservations.guide.finish.liveHelp'
          : 'reservations.guide.finish.liveHelpNoManage'
      );
    return tr('reservations.guide.finish.phoneOnlyHelp');
  });
  const showGroupsChip = computed(() => {
    const cfg = configuration.value;
    if (!cfg) return false;
    return (
      cfg.tableGroups.length === 0 ||
      maxBookableOnlineParty(cfg.tables, cfg.tableGroups) <
        cfg.settings.maxPartyOnline
    );
  });
  const depositsAvailable = computed(
    () => configuration.value?.settings.depositsAvailable ?? false
  );

  const activating = ref(false);
  async function turnOn(patch: IUpdateReservationSettingsDto) {
    if (busy.value) return;
    activating.value = true;
    let saved = false;
    try {
      saved = await context.savePatch(patch);
    } finally {
      if (alive) activating.value = false;
    }
    if (!saved || !alive) return;
    ui.showToast(AlertType.Success, tr('reservations.guide.finish.activated'));
    justActivated.value = true;
  }
  function activate() {
    if (onlineBlocker.value !== null) return;
    void turnOn({ enabled: true, onlineEnabled: true });
  }
  function activatePhoneOnly() {
    if (!phoneOnlyAllowed.value) return;
    void turnOn({ enabled: true, onlineEnabled: false });
  }
  function enableOnline() {
    if (onlineBlocker.value !== null) return;
    void turnOn({ onlineEnabled: true });
  }
  function openSection(section: string) {
    void context.exit({ tab: 'settings', section });
  }
  /** The guide reloads and resumes for the other branch. */
  async function switchBranch(target: IBranch) {
    if (busy.value) return;
    if (!(await confirmReservationBranchLeave()) || !alive) return;
    vueData.setCurrentBranch(target);
  }
</script>

<template>
  <div class="reservations-guide-finish" :class="`state-${state}`">
    <header class="finish-header">
      <div
        v-if="state !== 'ready'"
        class="finish-check"
        :class="{ animate: justActivated }"
      >
        <PosUiIcon name="check" :size="28" />
      </div>
      <h2 class="finish-title">{{ title }}</h2>
      <p class="finish-help">{{ help }}</p>
    </header>

    <div v-if="state === 'ready'" class="activate-block">
      <PosUiButton
        class="activate-button"
        size="lg"
        block
        icon="calendar-check-outline"
        :disabled="onlineBlocker !== null || busy"
        :loading="activating"
        @click="activate"
        >{{ $t('reservations.guide.finish.activate') }}</PosUiButton
      >
      <p v-if="onlineBlocker !== null" class="blocked-line">
        <span>{{ blockedText }}</span>
        <button
          type="button"
          class="link-button"
          :disabled="busy"
          @click="context.go(blockedStep)"
        >
          {{ blockedBackLabel }}
        </button>
      </p>
    </div>
    <div v-else-if="state === 'phoneOnly'" class="activate-block">
      <PosUiButton
        class="activate-button"
        size="lg"
        block
        icon="web"
        :disabled="onlineBlocker !== null || busy"
        :loading="activating"
        @click="enableOnline"
        >{{ $t('reservations.guide.finish.enableOnline') }}</PosUiButton
      >
      <p v-if="onlineBlocker !== null" class="blocked-line">
        <span>{{ blockedText }}</span>
        <button
          type="button"
          class="link-button"
          :disabled="busy"
          @click="context.go(blockedStep)"
        >
          {{ blockedBackLabel }}
        </button>
      </p>
    </div>
    <V2ReservationsGuideLinkCard
      v-else-if="branch"
      :branch-id="branch.id"
      :disabled="busy"
    />

    <section v-if="configuration" class="summary-section">
      <h3 v-if="state !== 'ready'" class="section-title">
        {{ $t('reservations.guide.finish.asConfigured') }}
      </h3>
      <div class="summary-list">
        <V2ReservationsGuideSummary
          :configuration="configuration"
          :branch="branch ?? undefined"
          :disabled="busy"
          @go="context.go($event)"
        />
        <V2ReservationsGuideRules />
      </div>
    </section>

    <p v-if="state === 'ready'" class="phone-only-line">
      <button
        type="button"
        class="link-button"
        :disabled="!phoneOnlyAllowed || busy"
        @click="activatePhoneOnly"
      >
        {{ $t('reservations.guide.finish.phoneOnly') }}
      </button>
    </p>

    <section v-else class="later-section">
      <h3 class="section-title">
        {{ $t('reservations.guide.finish.laterTitle') }}
      </h3>
      <div class="later-chips">
        <PosUiButton
          v-if="showGroupsChip"
          class="later-chip"
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="context.go('groups')"
          >{{ $t('reservations.guide.finish.laterGroups') }}</PosUiButton
        >
        <PosUiButton
          v-if="depositsAvailable"
          class="later-chip"
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="openSection('deposit')"
          >{{ $t('reservations.guide.finish.laterDeposit') }}</PosUiButton
        >
        <PosUiButton
          class="later-chip"
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="openSection('messages')"
          >{{ $t('reservations.guide.finish.laterPolicy') }}</PosUiButton
        >
        <PosUiButton
          class="later-chip"
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="openSection('dates')"
          >{{ $t('reservations.guide.finish.laterDates') }}</PosUiButton
        >
        <PosUiButton
          class="later-chip"
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="openSection('general')"
          >{{ $t('reservations.guide.finish.laterAdvanced') }}</PosUiButton
        >
        <PosUiButton
          v-for="other in otherBranches"
          :key="other.id"
          class="later-chip"
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="switchBranch(other)"
          >{{
            $t('reservations.guide.finish.laterBranch', { name: other.name })
          }}</PosUiButton
        >
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-finish {
    @apply tw-flex tw-flex-col tw-gap-5;
    p {
      @apply tw-m-0;
    }
    .finish-header {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .finish-check {
      @apply tw-mb-1 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-100 tw-text-emerald-700;
      &.animate {
        animation: reservations-guide-check 400ms ease-out both;
      }
    }
    .finish-title {
      @apply tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-neutral-900;
    }
    .finish-help {
      @apply tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }
    .activate-block {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .blocked-line {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-sm tw-text-amber-700;
    }
    .link-button {
      @apply tw-inline-flex tw-min-h-11 tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-50;
      }
    }
    .section-title {
      @apply tw-m-0 tw-mb-2 tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .summary-list {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200;
    }
    .phone-only-line {
      @apply tw-text-center;
      .link-button {
        @apply tw-text-neutral-500;
      }
    }
    .later-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    // Wrapping pills: long branch names must not widen the column.
    .later-chip {
      @apply tw-h-auto tw-min-h-11 tw-max-w-full tw-whitespace-normal tw-rounded-full tw-py-2 tw-text-left;
    }
  }
  @keyframes reservations-guide-check {
    from {
      transform: scale(0.4);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .reservations-guide-finish .finish-check.animate {
      animation: none;
    }
  }
</style>
